/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { Snackbar, Button, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { connect } from 'react-redux';
import { RootState } from '../../../store';
import styled from 'styled-components';
import StyledProps from '../../common/theme/AppThemeInterface';
import { isNil } from 'lodash';
import { getFlightLegServiceAlerts } from '../../../store/flightLegServiceAlerts/actions';
import { FTV_MAP_ID } from '../../../store/maps/actions';

const SnackbarRetryButton = styled(Button)`
  color: ${({ theme }: StyledProps) => theme.buttonSelectedColor};
`;
const MptFlightPathSnackbar = styled(Snackbar)`
  top: 345px;
  left: auto;
  right: 65px;
  transform: none;
  > div {
    background-color: ${({ theme }: StyledProps) => theme.secondaryBackgroundColor};
  }
`;
export interface ContainerStateProps {
  requestingFlightLegServiceAlertsError: string | undefined;
  selectedOverlaidFlightLegId: number | undefined;
  selectedOverlaidMissionPlanId: number | undefined;
  userId: string | undefined;
}

const MptFlightPathSnackbarContainer = (props: ContainerStateProps) => {
  const { requestingFlightLegServiceAlertsError, selectedOverlaidFlightLegId, selectedOverlaidMissionPlanId, userId } =
    props;
  const [mptFlightPathLoadingErrorSnackbarOpen, setMptFlightPathLoadingErrorSnackbarOpen] = useState<boolean>(false);
  useEffect(() => {
    if (requestingFlightLegServiceAlertsError) {
      setMptFlightPathLoadingErrorSnackbarOpen(true);
    }
  }, [requestingFlightLegServiceAlertsError]);

  const handleMptFlightPathLoadingErrorSnackbarClose = () => {
    setMptFlightPathLoadingErrorSnackbarOpen(false);
  };

  const reloadMptFlightPath = () => {
    setMptFlightPathLoadingErrorSnackbarOpen(false);
    if (!isNil(selectedOverlaidFlightLegId) && !isNil(selectedOverlaidMissionPlanId) && !isNil(userId)) {
      getFlightLegServiceAlerts(selectedOverlaidMissionPlanId, selectedOverlaidFlightLegId, userId);
    }
  };

  return (
    <div>
      <MptFlightPathSnackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={mptFlightPathLoadingErrorSnackbarOpen}
        autoHideDuration={999999}
        onClose={handleMptFlightPathLoadingErrorSnackbarClose}
        ContentProps={{
          'aria-describedby': 'message-id',
        }}
        message={<span id="message-id">Could not load MPT Flight Path</span>}
        action={[
          <SnackbarRetryButton key="retry" size="small" onClick={reloadMptFlightPath}>
            RETRY
          </SnackbarRetryButton>,
          <IconButton
            key="close"
            aria-label="Close"
            color="inherit"
            onClick={handleMptFlightPathLoadingErrorSnackbarClose}
          >
            <Close />
          </IconButton>,
        ]}
      />
    </div>
  );
};

export default connect(
  (state: RootState): ContainerStateProps => ({
    requestingFlightLegServiceAlertsError: state.flightLegServiceAlerts.requestingFlightLegServiceAlertsError,
    selectedOverlaidFlightLegId:
      state.flightLegs.mapContext[FTV_MAP_ID].uiState[state.flightLegs.currentContext]?.selectedOverlaidFlightLegId,
    selectedOverlaidMissionPlanId:
      state.flightLegs.mapContext[FTV_MAP_ID].uiState[state.flightLegs.currentContext]?.selectedOverlaidMissionPlanId,
    userId: state.session.tokens?.accessToken?.claims.email,
  }),
)(MptFlightPathSnackbarContainer);
